import { Directive, ElementRef, HostListener, Input } from '@angular/core';

@Directive({
  selector: '[appMaxNumber]'
})
export class MaxNumberDirective {
  @Input() appMaxNumber: number = 0;

  constructor(private el: ElementRef) {}

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): boolean {
    const input = this.el.nativeElement as HTMLInputElement;
    const currentValue = input.value || '';
    const key = event.key;

    // Allow control keys
    const controlKeys = [
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight',
      'ArrowUp', 'ArrowDown', 'Tab', 'Home', 'End'
    ];

    if (controlKeys.includes(key) || event.ctrlKey || event.metaKey) {
      return true;
    }

    // Check if key is a number
    if (!/^\d$/.test(key)) {
      return true;
    }

    // If max number is not set or is 0, allow normal typing
    if (!this.appMaxNumber || this.appMaxNumber <= 0) {
      return true;
    }

    // If field is empty, allow typing the first digit
    if (!currentValue || currentValue === '') {
      const numericValue = parseInt(key, 10);
      if (numericValue <= this.appMaxNumber) {
        return true;
      }
    }

    // Calculate new value after adding the digit
    const selectionStart = input.selectionStart || 0;
    const selectionEnd = input.selectionEnd || 0;
    const newValue = currentValue.substring(0, selectionStart) +
                     key +
                     currentValue.substring(selectionEnd);

    const numericValue = parseInt(newValue, 10);

    // Prevent typing if new value exceeds max limit
    if (numericValue > this.appMaxNumber) {
      event.preventDefault();
      return false;
    }

    return true;
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): boolean {
    const input = this.el.nativeElement as HTMLInputElement;
    const pastedData = event.clipboardData?.getData('text') || '';

    // Check if pasted data contains only numbers
    if (!/^\d+$/.test(pastedData)) {
      return true;
    }

    // If max number is not set or is 0, allow normal paste
    if (!this.appMaxNumber || this.appMaxNumber <= 0) {
      return true;
    }

    const currentValue = input.value || '';
    const selectionStart = input.selectionStart || 0;
    const selectionEnd = input.selectionEnd || 0;

    // Calculate new value after paste
    const newValue = currentValue.substring(0, selectionStart) +
                     pastedData +
                     currentValue.substring(selectionEnd);

    const numericValue = parseInt(newValue, 10);

    // Prevent paste if new value exceeds max limit
    if (numericValue > this.appMaxNumber) {
      event.preventDefault();
      return false;
    }

    return true;
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    // Additional safeguard in case value is set through other means
    if (!this.appMaxNumber || this.appMaxNumber <= 0) {
      return;
    }

    const input = this.el.nativeElement as HTMLInputElement;
    const value = parseInt(input.value, 10);

    if (!isNaN(value) && value > this.appMaxNumber) {
      input.value = this.appMaxNumber.toString();
      // Dispatch event to update Angular forms
      const evt = new Event('input', { bubbles: true });
      input.dispatchEvent(evt);
    }
  }
}
